/**
 * The attributes that identify a youth baseball team: how old it plays, how
 * competitively, and in what season. A name alone does not identify a team —
 * "River Bandits" is a different team at 9U AAA than at 12U Major.
 *
 * Declared the same way as `usStates.ts`: the display list comes first and IS
 * the source of truth, and the Zod validator is derived from it, so the picker
 * and the API can never drift apart.
 */
import { z } from "zod";
/**
 * Age groups in DISPLAY order, which is also numeric order. Do not sort this
 * list at a call site — a string sort puts "10U" before "6U".
 */
export const AGE_GROUPS = [
    "6U", "7U", "8U", "9U", "10U", "11U", "12U",
    "13U", "14U", "15U", "16U", "17U", "18U",
];
export const ageGroupSchema = z.enum(AGE_GROUPS);
/** Competitive classification, ascending. The stored value is the display value. */
export const CLASSIFICATIONS = ["A", "AA", "AAA", "Major"];
export const classificationSchema = z.enum(CLASSIFICATIONS);
/** Season terms in chronological order within a year. */
export const SEASON_TERMS = ["spring", "summer", "fall"];
/**
 * A season is a term AND a year, kept as a pair rather than the display string
 * "Spring 2026" because the string cannot be ordered: "2026-fall" sorts before
 * "2026-spring". My Teams groups by season, so ordering has to be real.
 */
export const seasonSchema = z.object({
    term: z.enum(SEASON_TERMS),
    year: z.number().int().min(2000).max(2100),
});
/** A Record so adding a term breaks the build here. */
const TERM_LABELS = {
    spring: "Spring",
    summer: "Summer",
    fall: "Fall",
};
export function formatSeason(season) {
    return `${TERM_LABELS[season.term]} ${season.year}`;
}
/** Sort rank within a year; index in SEASON_TERMS is already chronological. */
const termRank = (term) => SEASON_TERMS.indexOf(term);
/** Positive when `a` is later than `b`. Sort descending for newest-first. */
export function compareSeasons(a, b) {
    return a.year - b.year || termRank(a.term) - termRank(b.term);
}
/**
 * The seasons a picker offers, derived from the current date rather than
 * hardcoded — so the list stays current with no code change, ever.
 *
 * Newest first: a coach is far more likely to be creating a team for the
 * coming season than for a past one, and the list is short enough that the
 * tail costs nothing.
 */
export function seasonOptions(now) {
    const thisYear = now.getFullYear();
    const options = [];
    for (const year of [thisYear + 1, thisYear, thisYear - 1]) {
        for (let i = SEASON_TERMS.length - 1; i >= 0; i--) {
            options.push({ term: SEASON_TERMS[i], year });
        }
    }
    return options;
}
/** Stable `<option value>` encoding. Sorts correctly by year but not by term — use compareSeasons to order. */
export function seasonToValue(season) {
    return `${season.year}-${season.term}`;
}
/** Inverse of seasonToValue; null for anything that is not a valid season. */
export function seasonFromValue(value) {
    const parts = value.split("-");
    if (parts.length !== 2)
        return null;
    const [year, term] = parts;
    // Number() accepts whitespace, "+", "0x…", and "2e3" — which would parse
    // "2e3-fall" as the year 2000. The stored form is always four digits.
    if (!/^\d{4}$/.test(year))
        return null;
    const parsed = seasonSchema.safeParse({ term, year: Number(year) });
    return parsed.success ? parsed.data : null;
}
/**
 * "9U AAA · Spring 2026" — everything about a team except its name.
 *
 * Every part is treated as possibly absent even though two of them are typed
 * required, because a row stored before the backfill reaches here through an
 * unvalidated cast. An incomplete label beats the string "undefined".
 */
export function teamAttrs(team) {
    const parts = [];
    const grade = [team.ageGroup, team.classification].filter(Boolean).join(" ");
    if (grade)
        parts.push(grade);
    if (team.season)
        parts.push(formatSeason(team.season));
    return parts.join(" · ");
}
/** The full one-line label. Safe as `<option>` text — it returns a plain string. */
export function teamLabel(team) {
    const attrs = teamAttrs(team);
    return attrs ? `${team.name} · ${attrs}` : team.name;
}
//# sourceMappingURL=teamAttributes.js.map